/** The browser matrix (WC-29): current and previous major of Chromium, Firefox and Safari, else read-only. */
import { h } from "./dom.ts";

/** The browser families, the only thing about the browser that leaves it (RUM, WC-30). */
export const FAMILIES = ["chromium", "firefox", "safari", "other"] as const;
export type Family = (typeof FAMILIES)[number];

/** The current major per family; the previous major is also supported (updated each release train). */
export const CURRENT_MAJOR: Readonly<Record<Exclude<Family, "other">, number>> = {
  chromium: 141,
  firefox: 143,
  safari: 26,
};

export interface Support {
  readonly supported: boolean;
  readonly family: Family;
  readonly major: number | null;
  readonly reason: string | null;
}

export interface Environment {
  readonly userAgent: string;
  readonly features: Readonly<Record<string, boolean>>;
}

const REQUIRED_FEATURES = [
  "EventSource",
  "AbortController",
  "BroadcastChannel",
  "randomUUID",
  "Intl",
];

function major(userAgent: string, pattern: RegExp): number | null {
  const found = pattern.exec(userAgent);
  return found?.[1] === undefined ? null : Number(found[1]);
}

/** Classify a user agent into a browser family and its major version. */
export function identify(userAgent: string): { family: Family; major: number | null } {
  const chromium = major(userAgent, /(?:Chrome|Chromium|CriOS|Edg|EdgA)\/(\d+)/);
  if (chromium !== null) {
    return { family: "chromium", major: chromium };
  }
  const firefox = major(userAgent, /(?:Firefox|FxiOS)\/(\d+)/);
  if (firefox !== null) {
    return { family: "firefox", major: firefox };
  }
  const safari = major(userAgent, /Version\/(\d+)[\d.]* .*Safari\//);
  if (safari !== null) {
    return { family: "safari", major: safari };
  }
  return { family: "other", major: null };
}

/** Detect support by feature detection plus the UA major; unsupported means read-only. */
export function detectSupport(environment: Environment): Support {
  const { family, major: version } = identify(environment.userAgent);
  const missing = REQUIRED_FEATURES.filter((name) => environment.features[name] !== true);
  if (missing.length > 0) {
    return { supported: false, family, major: version, reason: `missing ${missing.join(", ")}` };
  }
  if (family === "other" || version === null) {
    return { supported: false, family, major: version, reason: "an unrecognised browser" };
  }
  if (version < CURRENT_MAJOR[family] - 1) {
    return {
      supported: false,
      family,
      major: version,
      reason: `${family} ${String(version)} is older than the previous major`,
    };
  }
  return { supported: true, family, major: version, reason: null };
}

/** The feature table of the live window. */
export function liveEnvironment(win: Window): Environment {
  return {
    userAgent: win.navigator.userAgent,
    features: {
      EventSource: "EventSource" in win,
      AbortController: "AbortController" in win,
      BroadcastChannel: "BroadcastChannel" in win,
      randomUUID: typeof win.crypto.randomUUID === "function",
      Intl: "Intl" in win,
    },
  };
}

/** The read-only banner for an unsupported browser, or `null`. */
export function renderSupportBanner(support: Support): HTMLElement | null {
  if (support.supported) {
    return null;
  }
  return h(
    "div",
    {
      class: "state-banner state-unsupported",
      attrs: { role: "alert", "data-state": "unsupported" },
    },
    h("span", { class: "state-symbol", attrs: { "aria-hidden": "true" } }, "!"),
    h(
      "span",
      { class: "state-text" },
      `This browser is not supported (${support.reason ?? "unknown"}). The page is read-only; every command control is disabled.`,
    ),
  );
}
